import {envIsDev, envIsServerDeployment} from '../config';
import {type Maybe} from '../generated/graphql';
import {isMountedInShell} from '../shellRemote';
import {type Viewer} from '../state/viewer/types';
import {type UserInfo} from '../types/graphql';
import {getStringValueFromQueryString} from './query-string';
import wait from './wait';

function formatCompanyForAnalytics(org: Viewer['organizations'][0]) {
  return {
    // ID is stable and will preserve analytics history if the
    // org is ever renamed.
    id: org.id,
    name: org.name,
    website: null,
  };
}

type FormattedSurveyAnswers = {
  howOften?: string;
  howOftenDetails?: string;
  trainReasons?: string[];
  trainReasonsDetails?: string;
  frameworks?: string[];
  frameworksDetails?: string;
  broughtBy?: string[];
  broughtByDetails?: string;
  teamSize?: string;
  teamSizeDetails?: string;
  learningFrom?: string[];
  learningFromDetails?: string;
  whichTools?: string[];
  whichToolsOther?: string;
  mlUseCases?: string[];
  mlUseCasesOther?: string;
  wandbDiscoveryOpts?: string[];
  wandbDiscoveryOptsOther?: string;
};

function formatSurveyAnswersForAnalytics(
  userInfo?: UserInfo
): FormattedSurveyAnswers {
  return userInfo
    ? {
        howOften: userInfo.howOften,
        howOftenDetails: userInfo.howOftenDetails,
        trainReasons: onboardingAnswersToArray(userInfo.trainReasons),
        trainReasonsDetails: userInfo.trainReasonsDetails,
        frameworks: onboardingAnswersToArray(userInfo.frameworks),
        frameworksDetails: userInfo.frameworksDetails,
        broughtBy: onboardingAnswersToArray(userInfo.broughtBy),
        broughtByDetails: userInfo.broughtByDetails,
        teamSize: userInfo.teamSize,
        teamSizeDetails: userInfo.teamSizeDetails,
        learningFrom: onboardingAnswersToArray(userInfo.learningFrom),
        learningFromDetails: userInfo.learningFromDetails,
        whichTools: onboardingAnswersToArray(userInfo.whichTools),
        whichToolsOther: userInfo.whichToolsOther,
        mlUseCases: onboardingAnswersToArray(userInfo.mlUseCases),
        mlUseCasesOther: userInfo.mlUseCasesOther,
        wandbDiscoveryOpts: onboardingAnswersToArray(
          userInfo.wandbDiscoveryOpts
        ),
        wandbDiscoveryOptsOther: userInfo.wandbDiscoveryOptsOther,
      }
    : {};
}

type FormattedMetadata = {
  teamCount?: number;
  team?: string;
  teams?: string;
  isPaid?: boolean;
};

type ViewerForAnalytics = FormattedMetadata &
  FormattedSurveyAnswers & {
    email: string;
    username?: string;
    name: string;
    createdAt?: string;
    website?: string;
    description?: string;
    organization: string;
    company: {
      id: string;
      name: string;
      website: null;
    } | null;
    deploymentId?: string;
  };

async function formatViewerForAnalytics(
  viewer: Viewer,
  deploymentId: Maybe<string> | undefined
): Promise<ViewerForAnalytics> {
  let organization = '';
  let company = null;
  const organizations = viewer.organizations ?? [];
  const teamEdges = viewer.teams?.edges ?? [];

  if (organizations.length > 0) {
    organization = organizations[0].name;
    company = formatCompanyForAnalytics(organizations[0]);
  }

  const envIsServer = envIsServerDeployment;
  const {username, email, name} = viewer;

  if (envIsServer) {
    // since there is no 'organization' for w&b server
    company = {
      id: deploymentId ?? viewer.id,
      name: '',
      website: null,
    };
  }

  const metadata: FormattedMetadata = {};
  if (teamEdges.length > 0 && !envIsServer) {
    metadata.teamCount = teamEdges.length;
    metadata.team = viewer.entity;
    // TODO: Segment doesn't seem to support arrays
    metadata.teams = teamEdges.map(e => e.node.name).join(', ');
    metadata.isPaid = teamEdges.some(e => e.node.isPaid);
  }
  const surveyAnswers = formatSurveyAnswersForAnalytics(viewer.userInfo);

  return {
    email,
    username,
    name,
    createdAt: envIsServer ? undefined : viewer.createdAt,
    website: viewer.userInfo?.websiteUrl,
    description: envIsServer ? undefined : viewer.userInfo?.bio,
    company,
    organization,
    ...surveyAnswers,
    ...metadata,
    deploymentId: deploymentId ?? undefined,
  };
}

/**
 * Identify with analytics platforms by viewer.
 *
 * Under the Forge shell, Segment identity belongs to the shell: it owns the
 * session and no remote can be trusted to identify the viewer on another
 * remote's behalf. See `services/shell/src/analytics/viewerTraits.ts`, which
 * this function was ported into. Chameleon and Mixpanel are still this app's,
 * so they stay on both paths.
 */
export async function identifyWithViewer(
  viewer?: Viewer,
  deploymentId?: Maybe<string> | undefined
) {
  if (!viewer?.email) {
    return;
  }
  const shellOwnsSegmentIdentity = isMountedInShell();

  const viewerForAnalytics = await formatViewerForAnalytics(
    viewer,
    deploymentId
  );
  if (window.analytics && !shellOwnsSegmentIdentity) {
    window.analytics.identify(viewer.id, viewerForAnalytics);

    for (const edge of viewer.teams?.edges ?? []) {
      const team = edge.node;
      if (team.isTeam) {
        window.analytics.group(team.name, {
          entityName: team.name,
          isPaid: team.isPaid,
        });
      }
    }
  }
  if (window.chmln) {
    const envIsServer = envIsServerDeployment;
    if (envIsServer) {
      const {email: userEmail, name: userName, username} = viewerForAnalytics;
      window.chmln.identify(viewer.analyticsId, {
        // REQUIRED, the unique ID of each user in your database (e.g. 23443 or "690b80e5f433ea81b96c9bf6")
        email: userEmail,
        name: userName,
        username,
      });
    } else {
      window.chmln.on('after:profile', () => {
        window.analytics?.track('Chameleon Page View', {
          testing_id: window.chmln?.data?.profile.attributes.percent,
        });
      });
    }
  }
  if (window.mixpanel) {
    window.mixpanel.init('8370c94cce01d7b850ac2c559a118066', {
      debug: envIsDev,
      track_pageview: true,
      persistence: 'localStorage',
      record_sessions_percent: 1,
    });
  }
}

function getZendeskChatContainer(): HTMLElement | null {
  return document.getElementById('launcher')?.parentElement ?? null;
}

function getZendeskChatLauncher(): HTMLElement | null {
  return document.getElementById('zendesk-launcher');
}

async function hideOrShowElWithRetry(
  getEl: () => HTMLElement | null,
  show: boolean,
  tries = 30,
  intervalMS = 100
): Promise<void> {
  for (let i = 0; i < tries; i++) {
    const el = getEl();
    if (el != null) {
      el.style.display = show ? 'block' : 'none';
      return;
    }
    if (i >= tries - 1) {
      return;
    }
    await wait(intervalMS);
  }
}

export async function hideZendeskChat(): Promise<void> {
  await hideOrShowElWithRetry(getZendeskChatContainer, false);
  await hideOrShowElWithRetry(getZendeskChatLauncher, false);
}

export async function showZendeskChat(): Promise<void> {
  await hideOrShowElWithRetry(getZendeskChatContainer, true);
  await hideOrShowElWithRetry(getZendeskChatLauncher, true);
}

export function trackWBReferrer(): void {
  const wbReferrer = getStringValueFromQueryString('wbreferrer');
  if (wbReferrer) {
    window.analytics?.track(`User Sent By W&B Referrer`, {
      referrer: wbReferrer,
    });
  }
}

function onboardingAnswersToArray(response?: {
  [key: string]: boolean | undefined;
}): string[] | undefined {
  if (response == null) {
    return;
  }

  const output = Object.keys(response).reduce((acc, key) => {
    if (response[key]) {
      acc.push(key);
    }
    return acc;
  }, [] as string[]);

  return output;
}
